/**
 * The shared-chrome components, driven entirely by the screen contract.
 *
 * Story #5343 (Launch W1). The 2026-09-02 design-match audit scored all 143
 * approved mockups against the shipped code: 260 of 954 contracted components
 * present (27%). 317 of the 694 missing were shared chrome, absent from nearly
 * every screen. They are mounted ONCE, by Layout.tsx, and read the current
 * route's contract row -- 317 missing components across 143 screens is one
 * build, not 143 edits.
 *
 * THIS IS THE SECOND DESIGN. The first shipped on 2026-09-03 and Rob called it
 * "quite unintelligible": it spent roughly 700px on four stacked blocks before
 * any data appeared, truncated three prose fields to about forty characters
 * each, and drew a second copy of chrome the pages already had. He was shown
 * three reworked options with rendered screenshots and chose B -- one header
 * line, ownership on a second line with everything else behind a disclosure,
 * the tab strip, then the page:
 *
 *   Environments > Overview  [Read-only]      E04  Copy link  Help
 *                                                          <- ScreenHeader
 *   Owned by Environments -> Overview  ·  Why am I seeing this? v
 *                                                          <- ScreenScopeLine
 *   Fleet  Compare  [Overview]  Services  Data  ...        <- DomainTabs
 *   ...page content, roughly 200px from the top...
 *   one note, carrying the contract's primary control      <- ScreenNote
 *
 * Scope is deliberately absent: the shell already renders ScopeRibbon, which
 * shows the environment AND changes it. Repeating it here would be the same
 * duplication in a new place.
 */
import React, { useState } from 'react';
import { NavLink, useLocation, matchPath } from 'react-router-dom';
import { Check, HelpCircle, Info, Link2 } from 'lucide-react';
import { useScreenContract, siblingScreens } from './useScreenContract';
import { parseCapability } from './capability';
import type { ScreenContract } from './screenContract.generated';

/**
 * Substitute the current URL's route params into a sibling screen's pattern.
 *
 * On /environments/env-prod/overview, sibling E05's pattern
 * '/environments/:environmentId/services' must become
 * '/environments/env-prod/services' -- otherwise every tab on every
 * entity-scoped screen links to a literal ':environmentId' and 404s.
 * Returns null when a parameter cannot be filled, so the tab renders as
 * inert text rather than a broken link.
 */
export function fillRouteParams(
  pattern: string,
  params: Readonly<Record<string, string | undefined>>,
): string | null {
  const segments = pattern.split('/');
  const filled: string[] = [];
  for (const segment of segments) {
    if (!segment.startsWith(':')) {
      filled.push(segment);
      continue;
    }
    const value = params[segment.slice(1)];
    if (!value) return null;
    filled.push(value);
  }
  return filled.join('/');
}

/** The route params of the CURRENT screen, for filling sibling patterns. */
function useCurrentParams(screen: ScreenContract | null): Record<string, string | undefined> {
  const { pathname } = useLocation();
  if (!screen) return {};
  const match = matchPath({ path: screen.routePattern, end: true }, pathname);
  return match?.params ?? {};
}

/** Where a sibling tab points, or null when it cannot be linked from here. */
function tabHref(
  sibling: ScreenContract,
  params: Record<string, string | undefined>,
): string | null {
  const path = fillRouteParams(sibling.routePattern, params);
  if (!path) return null;
  return sibling.routeQuery ? `${path}?${sibling.routeQuery}` : path;
}

/* ------------------------------------------------------------------ */
/* G02 -- breadcrumb, title, description                               */
/* ------------------------------------------------------------------ */

/**
 * The breadcrumb line, page title and purpose.
 *
 * The breadcrumb text is the contract's "Navigation position" cell verbatim
 * -- the mockups render exactly that string, e.g. "Environments -> select
 * immutable row -> Overview". It is prose describing the path taken, not a
 * series of links, so it is rendered as text with the screen's immutable id
 * beside it. "Copy link" satisfies G02's copy-canonical-link contract item.
 */
export const ScreenHeader: React.FC<{
  screen: ScreenContract;
  onOpenHelp?: () => void;
}> = ({ screen, onOpenHelp }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is unavailable (insecure context, denied permission). Say
      // nothing rather than claim a copy that did not happen.
    }
  };

  return (
    <div className="mb-3" data-testid="screen-header" data-screen-id={screen.id}>
      {/* ONE line: where you are, what it is, what it may do -- then the
          per-screen tools, right-aligned and small. The first attempt gave
          each of these its own row and a paragraph of purpose besides, which
          cost ~700px before any data. */}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <span className="text-xs text-slate-400">{screen.lhs}</span>
        <span className="text-slate-300" aria-hidden="true">
          &rsaquo;
        </span>
        <h1 className="text-xl font-bold tracking-tight text-iflow-900">{screen.tab}</h1>
        {/* The capability badge used to sit here, and on an estate-wide view it
            printed "Read-only" directly beneath the scope ribbon already saying
            "All environments - read-only". Rob's screenshot of E01 shows the
            same fact stated twice, one line apart, which is what made the
            header read as noise. The approved mockups show no such badge at
            all: their header is a breadcrumb, a title and a real subtitle. The
            capability is not lost -- it is in the "Why am I seeing this?"
            disclosure under "This screen", with the contract's own wording,
            which is where the tier detail was already duplicated anyway. */}

        <span className="flex-1" />

        <span
          title="Immutable screen id from the approved design"
          className="font-mono text-[11px] text-slate-400"
        >
          {screen.id}
        </span>
        <button
          type="button"
          onClick={copy}
          title="Copy this screen's canonical link"
          className="inline-flex items-center gap-1 rounded-md border border-iflow-900/15 px-2 py-1 text-[11.5px] font-medium text-iflow-800 hover:bg-iflow-50"
        >
          {copied ? <Check className="h-3 w-3" /> : <Link2 className="h-3 w-3" />}
          {copied ? 'Copied' : 'Copy link'}
        </button>
        {onOpenHelp && (
          <button
            type="button"
            onClick={onOpenHelp}
            title={screen.helpId ? `Contextual help (${screen.helpId})` : 'Contextual help'}
            className="inline-flex items-center gap-1 rounded-md border border-iflow-900/15 px-2 py-1 text-[11.5px] font-medium text-iflow-800 hover:bg-iflow-50"
          >
            <HelpCircle className="h-3 w-3" />
            Help
          </button>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Who owns this, and why you are seeing it                            */
/* ------------------------------------------------------------------ */

/**
 * One line naming the canonical owner, with the rest on demand.
 *
 * The first attempt gave Focus, Resolved owner and Scope evidence a column
 * each and truncated all three to about forty characters, so every one read
 * as "Approvals visible to the signed-in sub...". Truncated prose is not
 * information. The owner is the one fact worth a permanent line -- it tells
 * an operator which screen actually changes this data -- and everything else
 * moves behind a disclosure that shows it in full when asked.
 *
 * Deliberately does NOT restate the environment. The shell already renders
 * ScopeRibbon, which both shows the scope AND lets you change it; repeating
 * "All environments" here would be the same duplication in a new place.
 */
export const ScreenScopeLine: React.FC<{ screen: ScreenContract }> = ({ screen }) => {
  const capability = parseCapability(screen.mode);

  return (
    <div className="mb-3 text-xs text-slate-500" data-testid="screen-scope-line">
      {/* THE PURPOSE, not the owner (2026-09-06).
          This line used to lead with "Owned by {screen.owner}" -- e.g. on E01,
          "Owned by Environment registry and lifecycle read model; Operations,
          Releases, Commercial and Administration remain canonical domain
          owners". That is the workbook's Canonical-owner column: build-team
          prose naming which service owns the data, shown to an operator as if
          it were an explanation. Rob's word for it was slop, and he was right.
          Meanwhile `purpose` -- the one column written as a sentence for a
          human, present for all 143 screens -- was rendered nowhere at all.
          The approved mockups settle it: every one shows a title and a plain
          subtitle, and none shows an owner, a mode or a screen id. The owner
          has not been deleted, only demoted into the disclosure below, where
          someone asking "why am I seeing this?" is the person it was written
          for. */}
      <span>{screen.purpose || screen.owner || 'Not stated in the contract'}</span>
      <span className="mx-2 text-slate-300" aria-hidden="true">
        &middot;
      </span>
      <details className="inline-block align-top">
        <summary className="cursor-pointer list-none text-iflow-700 hover:text-iflow-900">
          Why am I seeing this? &#9662;
        </summary>
        <dl className="mt-2 grid max-w-4xl grid-cols-[6.5rem_1fr] gap-x-4 gap-y-1.5 rounded-lg border border-iflow-900/10 bg-white p-3 text-left">
          <dt className="font-semibold text-slate-600">Focus</dt>
          <dd className="text-slate-600">{screen.focus || 'Not stated in the contract'}</dd>

          {/* The owner moved HERE from the visible line above, where it was the
              first thing an operator read. It answers "which service is
              authoritative for what I am looking at" -- a real question, but
              the one a person asks second, after they know what the screen is
              for. */}
          <dt className="font-semibold text-slate-600">Owned by</dt>
          <dd className="text-slate-600">{screen.owner || 'Not stated in the contract'}</dd>
          <dt className="font-semibold text-slate-600">This screen</dt>
          <dd className="text-slate-600">
            {capability.summary ?? screen.mode}
            {/* The badge shows only what THIS screen does, so a cell that also
                names a handed-off tier ("...changes hand off to Identity
                R2/R3") would otherwise lose that half. Show the contract's own
                words underneath, always. */}
            {capability.summary && (
              <span className="mt-0.5 block text-[11px] text-slate-400">{screen.mode}</span>
            )}
          </dd>

          {screen.evidence && (
            <>
              <dt className="font-semibold text-slate-600">Evidence</dt>
              <dd className="font-mono text-[11px] text-slate-400">{screen.evidence}</dd>
            </>
          )}
        </dl>
      </details>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* The per-domain tab strip                                            */
/* ------------------------------------------------------------------ */

const TAB_BASE =
  '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors';

/**
 * One tab per screen sharing this screen's domain AND group, in workbook
 * order -- so Administration -> Identity shows its six screens, and
 * Environments shows all eighteen.
 *
 * Underline idiom, matching the mockups. The four hand-written domain navs
 * (CustomersNav, KnowledgeNav, AdministrationNav, OperationsNav) used a pill
 * idiom instead and between them covered 19 of 143 screens; they now stand
 * down via ShellChromeContext rather than being deleted, so nothing breaks if
 * a route has no contract row.
 */
/**
 * Working screens that belong in a domain's tab strip but are not in the
 * approved contract, so `siblingScreens` can never find them.
 *
 * These are RESTORATIONS, not additions. Every one had a place in the legacy
 * sidebar and lost it when the eight-item G01 sidebar replaced that nav: the
 * page kept working and became reachable only by typing its URL. The approved
 * design's own pattern is "each domain opens its landing page and the
 * sub-screens are reached from in-domain", so in-domain tabs -- not a list at
 * the bottom of one page -- is what putting them back actually means.
 *
 * They stay here rather than in screenContract.generated.ts because that file
 * is GENERATED from the launch workbook and is overwritten; hand-editing it
 * would be silently undone on the next refresh. Each entry leaves when its
 * contract screen reaches parity (A21 gaining VM lifecycle controls, A14 a
 * real key table, O11 the full ingestion activity view) -- at which point the
 * legacy page can go too.
 */
const RESTORED_DOMAIN_TABS: Readonly<Record<string, ReadonlyArray<{ tab: string; href: string }>>> = {
  Operations: [
    { tab: 'Live metrics', href: '/monitor' },
    { tab: 'Ingestion activity', href: '/monitor/activity' },
    { tab: 'Ingestion control', href: '/ingestion' },
    { tab: 'Platform services', href: '/services' },
    { tab: 'Data stores', href: '/datastores' },
  ],
  Administration: [
    { tab: 'Developer VMs', href: '/developer-vms' },
    { tab: 'Encryption keys', href: '/key-management' },
    { tab: 'Cognito pools', href: '/access' },
  ],
};

export const DomainTabs: React.FC<{ screen: ScreenContract }> = ({ screen }) => {
  const params = useCurrentParams(screen);
  const { pathname } = useLocation();

  // Only tabs we can actually build a URL for. A sibling whose route needs an
  // id this URL does not carry used to render as inert grey text -- on E03
  // (Plan environment, no environment selected yet) that produced FOURTEEN
  // dead tabs and made the screen unreadable. Showing a control that cannot
  // be used is the same fault as the disabled buttons withdrawn in
  // scopeDecisions.tsx, so it gets the same answer: don't show it.
  const siblings = siblingScreens(screen)
    .map((sibling) => ({ sibling, href: tabHref(sibling, params) }))
    .filter((entry): entry is { sibling: ScreenContract; href: string } => entry.href !== null);

  const restored = RESTORED_DOMAIN_TABS[screen.lhs] ?? [];

  if (siblings.length + restored.length < 2) return null;

  return (
    <nav
      aria-label={`${screen.lhs} sections`}
      data-testid="domain-tabs"
      className="mb-4 flex flex-wrap gap-1 overflow-x-auto border-b border-iflow-900/10"
    >
      {siblings.map(({ sibling, href }) => {
        const isCurrent = sibling.id === screen.id;
        return (
          <NavLink
            key={sibling.id}
            to={href}
            end
            aria-current={isCurrent ? 'page' : undefined}
            className={`${TAB_BASE} ${
              isCurrent
                ? 'border-iflow-600 text-iflow-900'
                : 'border-transparent text-slate-500 hover:border-iflow-900/20 hover:text-iflow-800'
            }`}
          >
            {sibling.tab}
          </NavLink>
        );
      })}
      {restored.map(({ tab, href }) => {
        // These routes are outside the contract, so no contract row will ever
        // be the "current screen" while one is open -- the active state has to
        // come from the URL instead of from screen.id.
        const isCurrent = pathname === href;
        return (
          <NavLink
            key={href}
            to={href}
            end
            aria-current={isCurrent ? 'page' : undefined}
            className={`${TAB_BASE} ${
              isCurrent
                ? 'border-iflow-600 text-iflow-900'
                : 'border-transparent text-slate-500 hover:border-iflow-900/20 hover:text-iflow-800'
            }`}
          >
            {tab}
          </NavLink>
        );
      })}
    </nav>
  );
};

/* ------------------------------------------------------------------ */
/* The Focus / Owner / Scope-evidence bar                              */
/* ------------------------------------------------------------------ */


/* ------------------------------------------------------------------ */
/* The footer callouts                                                 */
/* ------------------------------------------------------------------ */

/**
 * ONE note, carrying the contract's own sentence about the primary control.
 *
 * The first attempt rendered two boxes derived from contract fields -- a
 * "Canonical owner" restatement and a capability restatement -- on top of
 * whatever callouts the page already had. On H03 that produced FOUR boxes
 * saying overlapping things, which is most of why Rob could not tell what the
 * screen was for.
 *
 * The owner now lives in ScreenScopeLine, and the capability in the header
 * badge, so neither is repeated here. What is left is the one thing the
 * contract says about this screen specifically and nothing else does: what
 * its primary control actually does. Screens with no primary control get no
 * note at all.
 *
 * The mockups' bespoke callout copy ("Passwords are never retrievable") lives
 * ONLY inside the 143 PNGs -- a search of every workbook sheet on 2026-09-02
 * found none of it -- so it is not available to render.
 */
export const ScreenNote: React.FC<{ screen: ScreenContract }> = ({ screen }) => {
  const capability = parseCapability(screen.mode);
  // Nothing worth saying -> say nothing. A note that restates the title is
  // furniture, and four of them is what made the first attempt unreadable.
  if (!screen.primaryControl) return null;

  const tone =
    capability.highest === 'R3'
      ? 'border-red-300 bg-red-50 text-red-800'
      : capability.highest === 'R2'
        ? 'border-amber-300 bg-amber-50 text-amber-900'
        : 'border-sky-200 bg-sky-50 text-sky-900';

  return (
    <p
      role="note"
      data-testid="screen-note"
      className={`mt-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${tone}`}
    >
      <Info className="mt-0.5 h-4 w-4 flex-shrink-0 opacity-70" aria-hidden="true" />
      <span>{screen.primaryControl}</span>
    </p>
  );
};

/* ------------------------------------------------------------------ */
/* The shell's mount points                                            */
/* ------------------------------------------------------------------ */

/** Everything the shell draws ABOVE the page: header, tabs, focus bar. */
export const ScreenChromeTop: React.FC<{ onOpenHelp?: () => void }> = ({ onOpenHelp }) => {
  const screen = useScreenContract();
  if (!screen) return null;
  return (
    <>
      <ScreenHeader screen={screen} onOpenHelp={onOpenHelp} />
      <ScreenScopeLine screen={screen} />
      <DomainTabs screen={screen} />
    </>
  );
};

/** Everything the shell draws BELOW the page: the footer callouts. */
export const ScreenChromeBottom: React.FC = () => {
  const screen = useScreenContract();
  if (!screen) return null;
  return <ScreenNote screen={screen} />;
};
